import React from 'react';

interface ContactLinkItem {
  label: string;
  value: string;
  action: string;
  type?: 'email' | 'phone' | 'external';
  target?: string;
}

export interface ContactInfoCardProps {
  variant?: string;
  email?: string;
  availabilityText?: string;
  columns?: number;
  items?: ContactLinkItem[];
  className?: string;
}

const ICONS: Record<string, string> = {
  email: '✉',
  phone: '📞',
  external: '🔗',
};

const ContactInfoCard: React.FC<ContactInfoCardProps> = ({
  variant,
  email,
  availabilityText,
  columns = 3,
  items,
  className = '',
}) => {
  // LinkGrid variant — render a grid of clickable contact cards
  if (variant === 'LinkGrid' && items && items.length > 0) {
    return (
      <div
        className={`contact-info-card ${className}`}
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
          gap: 'var(--spacing-6)',
        }}
      >
        {items.map((item, i) => (
          <a
            key={i}
            href={item.action}
            target={item.target}
            rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--spacing-3)',
              padding: 'var(--spacing-8) var(--spacing-6)',
              backgroundColor: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-xl)',
              textDecoration: 'none',
              color: 'inherit',
              transition: 'box-shadow var(--transition-base), transform var(--transition-base)',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLElement).style.boxShadow = 'var(--shadow-md)';
              (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLElement).style.boxShadow = 'none';
              (e.currentTarget as HTMLElement).style.transform = 'none';
            }}
          >
            <span style={{ fontSize: '2rem' }}>{ICONS[item.type ?? 'external'] ?? '🔗'}</span>
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', fontWeight: 'var(--font-weight-semibold)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {item.label}
            </span>
            <span style={{ fontSize: 'var(--font-size-base)', color: 'var(--color-primary)', fontWeight: 'var(--font-weight-medium)', textAlign: 'center' }}>
              {item.value}
            </span>
          </a>
        ))}
      </div>
    );
  }

  // Default variant — simple email + availability text
  return (
    <div className={`contact-info-card ${className}`} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
      {email && (
        <div>
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', marginBottom: 'var(--spacing-1)' }}>Email</p>
          <a href={`mailto:${email}`} style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-primary)', textDecoration: 'none' }}>{email}</a>
        </div>
      )}
      {availabilityText && <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>{availabilityText}</p>}
    </div>
  );
};

export default ContactInfoCard;
